// Amherst College: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Amherst's Common Data Set 2025-2026, Section C (Institutional Research
//    posts each CDS section as its own PDF on amherst.edu): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table, C8 test
//    policy, C21/C22 plans. C21 labels its early decision counts "For the
//    Fall 2024 entering class", so no early decision rate is shown.
//  - Amherst Admission, First-Year Applicants (deadlines for the application
//    due November 9, 2026 and January 5, 2027, decision timing) and the
//    Standardized Testing Policy page.
//  - Amherst, Tuition, Fees and Other Costs: the 2026-2027 student expense
//    budget. The CDS G1 is marked not final (its figures are 2025-2026), so
//    the college's own 2026-2027 budget is used. Amherst gives no total; the
//    total here is the sum of the items it prices.
//  - College Scorecard API, school id 164465 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Amherst Common Data Set 2025-2026, Section C',
        url: 'https://www.amherst.edu/system/files/C.%20First-Time%20First-Year%20Admission%202025-26_0.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Amherst Admission, First-Year Applicants',
        url: 'https://www.amherst.edu/admission/apply/firstyear',
        read: '2026-10-01',
    },
    testing: {
        label: 'Amherst Admission, Standardized Testing Policy',
        url: 'https://www.amherst.edu/admission/apply/firstyear/testing',
        read: '2026-10-01',
    },
    costs: {
        label: 'Amherst, Tuition, Fees and Other Costs (2026-2027)',
        url: 'https://www.amherst.edu/tuition',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?164465-Amherst-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'amherst-college',
    kbId: 'amherst_college',
    name: 'Amherst College',
    shortName: 'Amherst',
    location: 'Amherst, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Amherst visual identity colors); decoration only.
    branding: {
        primary: '#3F1F69',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/amherst_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 15819,
        admitted: 1222,
        enrolled: 485,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Massachusetts)', applied: 1061, admitted: 123 },
            { group: 'Out-of-state', applied: 8412, admitted: 1007 },
            { group: 'International', applied: 6346, admitted: 92 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Amherst admits to the college as a whole, and its Common Data Set reports one first-year acceptance rate with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Amherst has an early decision plan and no early action plan. The Common Data Set labels its early decision counts as fall 2024 figures, so it gives no early decision rate for fall 2025.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 9', decision: 'Early to mid December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Late March', binding: false },
        ],
        note: 'If admitted in Regular Decision, you reply by May 1, 2027. Early Decision applicants are admitted, deferred to Regular Decision, or denied.',
        source: 'firstYear',
        binding: 'Early Decision is binding: you agree not to be an early decision candidate at any other college and, if admitted, to withdraw your other applications and enroll at Amherst.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'The SAT or ACT is optional for first-year applicants. If you send scores, you may enter them yourself in the application, and Amherst uses your best combination (superscore). Official scores are needed only if you are admitted and enroll.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75330 },
            { item: 'Housing', amount: 10980 },
            { item: 'Meals', amount: 9340 },
            { item: 'Fees', amount: 710 },
            { item: 'Personal expenses', amount: 1800 },
            { item: 'Travel', amount: null },
        ],
        total: 98160,
        note: 'Travel varies by where you live, so it is not in the total. Required course materials are included in what Amherst charges, so there is no books line. Amherst lists the items but no total; $98,160 is their sum.',
        source: 'costs',
        netPrice: {
            average: 19536,
            byIncome: [
                { income: '$0 to $30,000', amount: 2424 },
                { income: '$30,001 to $48,000', amount: 3063 },
                { income: '$48,001 to $75,000', amount: 13814 },
                { income: '$75,001 to $110,000', amount: 17999 },
                { income: 'Over $110,000', amount: 49700 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Amherst\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
